import type { CrmPatientSummary } from "src-next/modules/crm-clinical/communications/types";
import { normalizeCpf } from "src-next/shared/utils/cpf";

export interface OrganizationRecipientSources {
  /** Pacientes atendidos (PatientOrganizationContext). Maior prioridade. */
  appointmentPatients?: CrmPatientSummary[];
  /** Pacientes com consentimento (Patient do MPI). */
  consentPatients?: CrmPatientSummary[];
  /** Clientes cadastrados (Person). Menor prioridade. */
  registeredClients?: CrmPatientSummary[];
}

/** CPF só com dígitos; vazio quando não for um CPF completo. */
function cpfKey(summary: CrmPatientSummary): string {
  const digits = normalizeCpf(String(summary.taxIdentifierValue ?? ""));
  return digits.length === 11 ? digits : "";
}

/**
 * Junta os três públicos sem repetir a mesma pessoa (lista "Todos").
 *
 * - Ordem de prioridade: atendido > consentimento > Person.
 *   Quem entra primeiro vence; os seguintes repetidos são descartados.
 * - Repetido = mesmo `patientId` ou mesmo CPF.
 * - O `patientId` é comparado sem o tipo do recurso. Isso também evita
 *   colisão na seleção do dialog de envio, que compara itens por `id`.
 * - Um Person com o CPF de um Patient fica fora daqui, mas continua
 *   visível no público "Clientes cadastrados".
 */
export function mergeOrganizationRecipients({
  appointmentPatients = [],
  consentPatients = [],
  registeredClients = [],
}: OrganizationRecipientSources): CrmPatientSummary[] {
  const seenIds = new Set<string>();
  const seenCpfs = new Set<string>();
  const result: CrmPatientSummary[] = [];

  const ordered = [...appointmentPatients, ...consentPatients, ...registeredClients];
  for (const summary of ordered) {
    const id = String(summary.patientId ?? "").trim();
    const cpf = cpfKey(summary);
    if (id && seenIds.has(id)) continue;
    if (cpf && seenCpfs.has(cpf)) continue;
    if (id) seenIds.add(id);
    if (cpf) seenCpfs.add(cpf);
    result.push(summary);
  }

  return result;
}

/**
 * Consentimento sem quem já está em pacientes atendidos.
 *
 * Função pura: não altera as listas recebidas, não faz IO e não tem cache.
 * A mesma pessoa é o mesmo `patientId` (sem espaços) ou o mesmo CPF de 11
 * dígitos (`cpfKey`, a regra do merge). Quem casa por qualquer um dos dois sai.
 * A ordem de quem permanece é a ordem do consentimento.
 * Lista de atendidos vazia devolve o consentimento inteiro (caso do ecommerce).
 */
export function consentPatientsNotAttended(
  consentPatients: CrmPatientSummary[],
  attendedPatients: CrmPatientSummary[],
): CrmPatientSummary[] {
  const attendedIds = new Set<string>();
  const attendedCpfs = new Set<string>();

  for (const summary of attendedPatients) {
    const id = String(summary.patientId ?? "").trim();
    const cpf = cpfKey(summary);
    if (id) attendedIds.add(id);
    if (cpf) attendedCpfs.add(cpf);
  }

  if (attendedIds.size === 0 && attendedCpfs.size === 0) return consentPatients;

  return consentPatients.filter((summary) => {
    const id = String(summary.patientId ?? "").trim();
    const cpf = cpfKey(summary);
    if (id && attendedIds.has(id)) return false;
    if (cpf && attendedCpfs.has(cpf)) return false;
    return true;
  });
}
